import type { WalletChanged } from '@pixelworld/shared';
import { useEffect, useState } from 'react';
import { da } from '../../i18n/da';
import { fetchWallet } from './wallet-api';
import styles from './CoinCounter.module.css';

/** How the counter hears about new rewards: `wallet:changed` on the room's socket. */
export interface WalletUpdates {
  onChange(listener: (change: WalletChanged) => void): () => void;
}

const GAIN_VISIBLE_MS = 1600;

/** The player's PixelCoins in the room's top bar. It bumps and shows "+10" when a reward lands. */
export function CoinCounter({ updates }: { updates: WalletUpdates }) {
  const [balance, setBalance] = useState<number | null>(null);
  const [gain, setGain] = useState<{ delta: number; key: number } | null>(null);

  useEffect(() => {
    let cancelled = false;
    fetchWallet().then(
      (result) => {
        // A reward that arrived first already carries a newer balance.
        if (!cancelled && result.ok) setBalance((current) => current ?? result.data.balance);
      },
      () => undefined,
    );
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | undefined;
    const unsubscribe = updates.onChange((change) => {
      setBalance(change.balance);
      setGain({ delta: change.delta, key: Date.now() });
      clearTimeout(timer);
      timer = setTimeout(() => setGain(null), GAIN_VISIBLE_MS);
    });
    return () => {
      unsubscribe();
      clearTimeout(timer);
    };
  }, [updates]);

  return (
    <div className={styles.counter} title={da.wallet.title}>
      <span className={styles.coin} aria-hidden="true" />
      {/* A new key restarts the bump. The two keys differ: siblings with the same key are left behind on screen. */}
      <strong
        key={`balance-${gain?.key ?? 0}`}
        className={gain ? `${styles.balance} ${styles.bump}` : styles.balance}
        data-testid="coin-balance"
        aria-label={balance === null ? undefined : da.wallet.balance(balance)}
      >
        {balance ?? '…'}
      </strong>
      {gain && (
        <span key={`gain-${gain.key}`} className={styles.gain} role="status">
          {da.wallet.gained(gain.delta)}
        </span>
      )}
    </div>
  );
}
